import { Static, Type } from '@sinclair/typebox';
import { PreviewGenerationStageSchema } from './lead-preview';
import { LeadAttributionSchema } from './tracking/lead-funnel';

// =====================================================================
// Mirror of Prisma enums (assistant.prisma) — keep in sync.
// =====================================================================

export enum LeadState {
  COLD = 'COLD',
  WARM = 'WARM',
  QUALIFIED = 'QUALIFIED',
  SUSPENS = 'SUSPENS',
  LOST = 'LOST',
  ONBOARDING = 'ONBOARDING',
  TRIAL = 'TRIAL',
  PAID = 'PAID',
  ACTIVE = 'ACTIVE',
  AT_RISK = 'AT_RISK',
  CHURNED = 'CHURNED',
  AMBASSADEUR = 'AMBASSADEUR',
  /**
   * « C'est un projet, pour plus tard » (quiz, écran 2 bis) : lead gardé avec sa date
   * (`lead.project_date`) et relancé par la séquence admin « Projet lointain ». Exclu PAR DÉFAUT
   * des vues leads, des compteurs et des files d'appel (`prospectLeadWhere`) : ce n'est pas un
   * prospect à traiter maintenant. Sort de cet état quand le quiz est repris (écran 3).
   */
  DISTANT_PROJECT = 'DISTANT_PROJECT',
}

export enum MessageRole {
  USER = 'USER',
  ASSISTANT = 'ASSISTANT',
  SYSTEM = 'SYSTEM',
  TOOL = 'TOOL',
}

export enum MessageChannel {
  WEB = 'WEB',
  WEB_WIDGET = 'WEB_WIDGET',
  WHATSAPP = 'WHATSAPP',
  MESSENGER = 'MESSENGER',
  INSTAGRAM = 'INSTAGRAM',
  EMAIL = 'EMAIL',
  SYSTEM = 'SYSTEM',
}

export enum ToolInvocationStatus {
  PENDING = 'PENDING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
  REFUSED = 'REFUSED',
}

// Cascade des relances Pulse SUSPENS (cf. §7.4 spec cible). Une row par relance
// planifiée par `schedule_relance`, scannée toutes les 10 min par le worker cron.
export enum TriggerRunStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

// =====================================================================
// Pulse — Qualifier playbook. Six-axis silent analysis grid the LLM uses
// during the qualification phase (chantier 1). Definitions come from
// docs/ai-assistant-architecture.md and the Pulse functional spec.
// =====================================================================

export enum LeadTier {
  TIER_1_BEAUTE = 'tier_1_beaute',
  TIER_1_BIEN_ETRE = 'tier_1_bien_etre',
  TIER_1_VTC = 'tier_1_vtc',
  TIER_2_GENERIQUE = 'tier_2_generique',
  /**
   * Médical conventionnel (médecin, dentiste, pharmacien, vétérinaire). Cible
   * compatible Propelsia (gestion d'agenda) mais avec des contraintes
   * spécifiques : vocabulaire « patients/clients », pas de pitch fidélisation
   * marketing (cartes cadeaux, push commerciaux), accent sur l'information
   * professionnelle, respect des règles déontologiques (sollicitation d'avis
   * limitée). Cf. section TIER_3_MEDICAL_POSTURE du qualifier_pro_v1 prompt.
   */
  TIER_3_MEDICAL = 'tier_3_medical',
  /**
   * Hors cible — commerce de produits (boutique physique ou en ligne), B2B,
   * SaaS, agence digitale, industrie. Pulse ne fait PAS la qualif produit ;
   * bascule directement sur le pitch d'affiliation pédagogique en 2 tours.
   */
  TIER_4_LOST = 'tier_4_lost',
}

export enum LeadPatrimoine {
  RIEN = 'rien',
  RESEAUX_SEULS = 'reseaux_seuls',
  FICHE_GOOGLE = 'fiche_google',
  SITE_AGENCE_EXISTANT = 'site_agence_existant',
  SITE_DIY = 'site_diy',
  PLATEFORME = 'plateforme',
  EPARPILLE = 'eparpille',
}

export enum LeadBesoin {
  VISIBILITE = 'visibilite',
  RESERVATION = 'reservation',
  INDEPENDANCE = 'independance',
  SITE_COMPLET = 'site_complet',
  LANCEMENT = 'lancement',
  MODERNISATION = 'modernisation',
  STRUCTURATION = 'structuration',
}

export enum LeadDouleur {
  DEPENDANCE_PLATEFORME = 'dependance_plateforme',
  INVISIBILITE_METIER = 'invisibilite_metier',
  INVESTISSEMENT_SANS_RETOUR = 'investissement_sans_retour',
  CHAOS_ORGANISATIONNEL = 'chaos_organisationnel',
  PLAFOND_DE_CROISSANCE = 'plafond_de_croissance',
  PEUR_DU_DEMARRAGE = 'peur_du_demarrage',
  SOLITUDE_PRO = 'solitude_pro',
  USURE_EMOTIONNELLE = 'usure_emotionnelle',
}

export enum LeadPosture {
  ENTHOUSIASTE = 'enthousiaste',
  PRAGMATIQUE = 'pragmatique',
  ANXIEUX_TECH = 'anxieux_tech',
  DEFENSIF = 'defensif',
  BAVARD = 'bavard',
  PRESSE = 'presse',
  VEUT_HUMAIN = 'veut_humain',
}

export enum LeadMaturite {
  LANCEMENT = 'lancement',
  ANS_1_3 = '1_3_ans',
  ANS_3_5 = '3_5_ans',
  ANS_5_PLUS = '5_plus_ans',
}

/**
 * @deprecated Supprimé dans la refonte spec cible 2026-05-23. Le routage se
 * fait désormais par phase (Comprendre / Démontrer / Inviter) selon ce que
 * l'intent detector extrait du message, sans axe orientation séparé.
 *
 * Conservé temporairement comme alias vide pour les migrations en cours.
 */
export enum LeadOrientation {
  /** @deprecated */ PAIN = 'pain',
  /** @deprecated */ GOAL = 'goal',
  /** @deprecated */ READY = 'ready',
  /** @deprecated */ NEUTRAL = 'neutral',
}

/**
 * Profils détectables au sein du Tier 4 (hors cible). Sert au pitch
 * d'affiliation qui adapte son ton selon le type de réseau du lead.
 */
export enum Tier4Profile {
  AGENCE_DIGITALE = 'agence_digitale',
  COACH_BUSINESS = 'coach_business',
  COMPTABLE = 'comptable',
  INFLUENCEUR_SECTEUR_SERVICES = 'influenceur_secteur_services',
  ANCIEN_INDE_FORMATEUR = 'ancien_inde_formateur',
  CONSULTANT_MARKETING_LOCAL = 'consultant_marketing_local',
  AUTRE = 'autre',
}

/**
 * Pulse — phases de la machine de qualification (refonte spec cible 2026-05-23).
 *
 * Refactor : on passe de 11 micro-states aux 5 phases canoniques de la spec.
 * Tous les tiers (1/2/3/4) passent par les mêmes 3 phases actives. Le contenu
 * sectoriel est injecté via `knowledge/sector_markers/*` selon le tier.
 *
 * - `phase-1-comprendre` : Tour 0 + 2 questions canoniques (patrimoine + résa)
 * - `phase-2-demontrer`  : escalier 4 marches + touche bras droit + preview
 *   (pour Tier 4, pivot affiliation au lieu de l'escalier)
 * - `phase-3-inviter`    : drive vers preview, réponses produit/tarif, webinaire
 * - `phase-4-suspens`    : cron-driven (relances) — pas appelé en conv
 * - `phase-5-escalate`   : cas exceptionnels (agressif, bug, support critique)
 *
 * Le Tour 0 figé (premier message assistant) est émis avant tout appel LLM,
 * dès `conversation.messages.count === 0`. Pas de state TOUR_0 dans l'enum.
 */
export enum PulseQualifierState {
  PHASE_1_COMPRENDRE = 'phase-1-comprendre',
  PHASE_2_DEMONTRER = 'phase-2-demontrer',
  PHASE_3_INVITER = 'phase-3-inviter',
  PHASE_4_SUSPENS = 'phase-4-suspens',
  PHASE_5_ESCALATE = 'phase-5-escalate',
}

// Helper for TypeBox Type.Union construction over a string enum's values.
// Deliberately untyped on the literal narrow (returns the broad TUnion shape)
// to keep the call sites happy without fighting tsc on T[keyof T] inference.
const enumLiteralUnion = (e: Record<string, string>): ReturnType<typeof Type.Union> =>
  Type.Union(Object.values(e).map((v): ReturnType<typeof Type.Literal> => Type.Literal(v)));

/**
 * Pulse — schéma typé du jsonb `lead.qualification` (refonte spec cible §9.3).
 *
 * Construit progressivement par l'intent detector (extraction continue) + le
 * runtime (post-actions tours), persisté dans `lead.qualification`. Chaque
 * champ est optionnel — le schéma se remplit au fil des conversations.
 */
export const LeadQualificationSchema = Type.Object({
  // === Identité (Tour 0 + extraction continue) ===
  nom_pro: Type.Optional(Type.String({ maxLength: 200 })),
  metier: Type.Optional(Type.String({ maxLength: 120 })),
  /** Verbatim du métier tel que le lead l'a dit ("esthé" → metier="esthéticienne", metier_raw="esthé"). */
  metier_raw: Type.Optional(Type.String({ maxLength: 120 })),
  ville: Type.Optional(Type.String({ maxLength: 120 })),
  /** Verbatim de "qu'est-ce qui t'amène", récolté Tour 0. */
  contexte_initial: Type.Optional(Type.String({ maxLength: 2000 })),

  // === Tier (déterministe via detectMetierTier + amendable par l'intent detector) ===
  tier: Type.Optional(enumLiteralUnion(LeadTier)),
  tier_confidence: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  /**
   * Tier d'origine (regex conversationnel) avant correction post-détection par
   * les `activity_labels` scrapés (Lot 4). Audit only — `tier` porte la valeur
   * corrigée. Jamais renseigné si aucune correction n'a eu lieu.
   */
  tier_corrected_from: Type.Optional(enumLiteralUnion(LeadTier)),
  /**
   * Activité HORS cœur de cible admise pour la prise de rendez-vous uniquement (boutique qui
   * prend des RDV, avocat, agence immobilière, école privée, consultant…). Collant : posé une
   * fois, Pulse ne parle plus que des fonctionnalités réelles (site, RDV, rappels, fidélisation).
   * Sert aussi à mesurer la conversion de la cible élargie.
   */
  cible_elargie: Type.Optional(Type.Boolean()),
  /**
   * Activité exclue sans appel (services sexuels, activité illégale, contenus adultes). Collant :
   * déclin sans porte de sortie, le tier ne se rouvre jamais automatiquement.
   */
  activite_exclue: Type.Optional(Type.Boolean()),

  // === Patrimoine digital (phase Comprendre, Q1+Q2 canoniques) ===
  has_google_business: Type.Optional(Type.Boolean()),
  google_business_url: Type.Optional(Type.String({ maxLength: 500 })),
  has_website: Type.Optional(Type.Boolean()),
  website_url: Type.Optional(Type.String({ maxLength: 500 })),
  has_booking_platform: Type.Optional(Type.Boolean()),
  booking_platform_url: Type.Optional(Type.String({ maxLength: 500 })),
  /** Synthèse calculée par le runtime à partir des champs ci-dessus. */
  patrimoine: Type.Optional(
    Type.Union([
      Type.Literal('rien'),
      Type.Literal('fiche_seule'),
      Type.Literal('site_seul'),
      Type.Literal('plateforme_seule'),
      Type.Literal('complet'),
      Type.Literal('eparpille'),
    ]),
  ),

  // === Signaux extraits en continu par l'intent detector ===
  /** Signaux de besoin captés en texte libre (ex: "dependance_plateforme", "visibilite", "fidelisation"). */
  signaux_besoin: Type.Optional(Type.Array(Type.String({ maxLength: 80 }), { maxItems: 12 })),
  /** Signaux de douleur captés (libre, ex: "no_show", "invisibilite", "marge_grignotee"). */
  signaux_douleur: Type.Optional(Type.Array(Type.String({ maxLength: 80 }), { maxItems: 12 })),
  /** Prestations mentionnées en clair ("coupe", "consultation 1h", "course aéroport"). */
  prestations_mentionnees: Type.Optional(Type.Array(Type.String({ maxLength: 120 }), { maxItems: 12 })),
  /** Équipe (nombre + type). */
  equipe: Type.Optional(
    Type.Object({
      nombre: Type.Optional(Type.Number()),
      type: Type.Optional(Type.String({ maxLength: 80 })),
    }),
  ),
  maturite: Type.Optional(Type.String({ maxLength: 80 })),

  // === Posture & douleur structurées (legacy, conservées pour debug) ===
  posture: Type.Optional(enumLiteralUnion(LeadPosture)),
  douleur: Type.Optional(enumLiteralUnion(LeadDouleur)),
  besoin: Type.Optional(enumLiteralUnion(LeadBesoin)),

  // === Parser web (dépend d'Alex — ingest_url) ===
  parser_output: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
  parser_status: Type.Optional(
    Type.Union([Type.Literal('pending'), Type.Literal('completed'), Type.Literal('failed')]),
  ),

  // === Preview ===
  /** Consentement explicite du lead pour scraping/génération preview (RGPD). */
  preview_consent_given_at: Type.Optional(Type.String({ format: 'date-time' })),
  /**
   * Périmètre du consentement, miroir de `professional.detection_consent_scope`
   * côté onboarding (cf. assertDetectionConsent). Trace légale + indique au
   * runtime quelles sources passer à `generate`. La garde backend
   * (`assertPreviewScrapeConsent`) refuse tout mode scraping si ce champ est
   * absent ou `declined`. `declined` = le lead a refusé → collecte
   * conversationnelle (from_scratch avec contenu fourni).
   */
  preview_consent_scope: Type.Optional(
    Type.Union([
      Type.Literal('declined'),
      Type.Literal('auto'),
      Type.Literal('auto+url'),
      Type.Literal('auto+photos'),
      Type.Literal('auto+url+photos'),
    ]),
  ),
  /** Description libre fournie PAR LE LEAD quand il a refusé le scraping (sert de base bio en from_scratch). */
  lead_provided_description: Type.Optional(Type.String({ maxLength: 2000 })),
  /**
   * Prestations détaillées — soit fournies par le lead (chemin collecte/refus),
   * soit repliées depuis l'extraction (prix/variants) pour un pitch concret en
   * phase `inviter`. Distinct de `prestations_mentionnees` (string[] plat).
   */
  prestations_detailed: Type.Optional(
    Type.Array(
      Type.Object({
        name: Type.String({ maxLength: 120 }),
        description: Type.Optional(Type.String({ maxLength: 500 })),
        price_eur: Type.Optional(Type.Number()),
        duration_min: Type.Optional(Type.Number()),
        // Catégorie d'origine (scrape/fiche) — préserve l'ARBORESCENCE catégories→prestations
        // à la matérialisation du catalogue au signup (`materializeCatalogFromExtraction`).
        // Sans ça, tout retombe sous une catégorie par défaut (arborescence perdue à l'import).
        category: Type.Optional(Type.String({ maxLength: 120 })),
      }),
      { maxItems: 20 },
    ),
  ),
  /**
   * Job de génération preview en cours. Set à true à l'enqueue du job BullMQ,
   * unset à completion (preview_url renseigné). Lu par le runtime pour gérer
   * l'attente : court-circuit `no_reply` sur filler ("ok", "j'attends"), sinon
   * brief `preview_pending_ps` injecté dans le compositeur.
   */
  preview_generation_pending: Type.Optional(Type.Boolean()),
  /**
   * Étape courante du job de génération (waouh v2 §3.3) — posée par le processor aux
   * jalons réels (lookup/photos/reviews/assembly, Phase B = enriching, fin = done).
   * Exposée au widget (bulle de progression) via GET widget/conversation.
   */
  preview_generation_stage: Type.Optional(Type.String({ maxLength: 20 })),
  /**
   * Horodatage de la dernière pose de `preview_generation_stage`. Sert de TTL côté
   * lecture : un stage non terminal (ex. `enriching`) trop vieux est traité comme
   * terminé — un worker mort en plein job (attempts: 1, pas de retry) ne doit pas
   * laisser la bannière « j'enrichis encore… » bloquée pour toujours.
   */
  preview_generation_stage_at: Type.Optional(Type.String({ format: 'date-time' })),
  /**
   * Dernier ÉCHEC de génération (job worker ou appel `generate` rejeté) sans preview livrée
   * (recette preprod 16/10/2026, B3). Lu par le dispatcher (plan B : construire ensemble, puis
   * from_scratch enrichi) et par le contexte compositeur (jamais « en cours » / « presque prêt »).
   * Effacé au prochain départ de job (`markPreviewGenerationStarted`) et à la livraison.
   */
  preview_failed_at: Type.Optional(Type.String({ format: 'date-time' })),
  preview_url: Type.Optional(Type.String({ maxLength: 500 })),
  preview_token: Type.Optional(Type.String({ maxLength: 120 })),
  preview_template: Type.Optional(Type.String({ maxLength: 80 })),
  preview_generated_at: Type.Optional(Type.String({ format: 'date-time' })),

  // === Webinaire ===
  webinar_registered_at: Type.Optional(Type.String({ format: 'date-time' })),
  webinar_session_id: Type.Optional(Type.String({ maxLength: 120 })),
  webinar_link_clicked_at: Type.Optional(Type.String({ format: 'date-time' })),

  // === Signup (bascule onboarding) ===
  signup_completed_at: Type.Optional(Type.String({ format: 'date-time' })),

  // === Affiliation (Tier 4) ===
  affiliation_program_signup_at: Type.Optional(Type.String({ format: 'date-time' })),

  // === Sortie de qualification ===
  qualification_status: Type.Optional(
    Type.Union([
      Type.Literal('active'),
      Type.Literal('qualified_signup'),
      Type.Literal('qualified_webinar'),
      Type.Literal('qualified_affiliation'),
      Type.Literal('suspens'),
      Type.Literal('lost'),
      Type.Literal('escalated'),
    ]),
  ),
  suspens_reason: Type.Optional(Type.String({ maxLength: 80 })),
  lost_reason: Type.Optional(Type.String({ maxLength: 80 })),
  lost_reason_verbatim: Type.Optional(Type.String({ maxLength: 500 })),
  escalate_reason: Type.Optional(Type.String({ maxLength: 80 })),

  // === Tracking & scoring ===
  scoring_axes: Type.Optional(
    Type.Object({
      fit: Type.Number({ minimum: 0, maximum: 1 }),
      intention: Type.Number({ minimum: 0, maximum: 1 }),
      capacite: Type.Number({ minimum: 0, maximum: 1 }),
    }),
  ),
  current_phase: Type.Optional(
    Type.Union([Type.Literal('comprendre'), Type.Literal('demontrer'), Type.Literal('inviter')]),
  ),
  phase_history: Type.Optional(
    Type.Array(
      Type.Object({
        phase: Type.String(),
        entered_at: Type.String({ format: 'date-time' }),
      }),
      { maxItems: 50 },
    ),
  ),

  // === Hors-scope tracking (§12.5 spec) ===
  /**
   * Compteur de tours consécutifs hors-scope. Reset à 0 dès que le lead revient
   * on-scope. Au 2e essai consécutif → marker [[LOST:hors_scope]] auto.
   */
  out_of_scope_count: Type.Optional(Type.Number({ minimum: 0 })),
  out_of_scope_last_category: Type.Optional(Type.String({ maxLength: 80 })),
  out_of_scope_last_at: Type.Optional(Type.String({ format: 'date-time' })),

  // === Pulse journey tracking (anti-redites, ajouté 24-mai-soir) ===
  /**
   * Tracker des actions Pulse déjà effectuées dans la conversation.
   * Auto-rempli par `detectAndUpdateJourney()` côté runtime à chaque tour
   * Pulse via regex sur les wordings canoniques. Lu par les state-builders
   * pour éviter de re-poser une question / re-présenter l'escalier / etc.
   *
   * Solution structurelle au pattern "Pulse répète X parce qu'il n'a pas
   * de mémoire explicite de ce qu'il a déjà fait" — cf. docs/pulse-recap.md
   * §session 4 décision PulseJourney.
   */
  pulse_journey: Type.Optional(
    Type.Object({
      /** Q1 patrimoine fiche/site posée. */
      q1_asked_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Q2 solution réservation posée. */
      q2_asked_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Escalier 4 marches présenté en intégralité. */
      escalier_presented_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Proposition preview ("Je te génère ta preview ?"). */
      preview_proposed_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Consentement RGPD demandé ("je cherche tes données publiques ?"). Gate du handshake consent-first. */
      consent_asked_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Lead a accepté explicitement la recherche de données publiques (posé par le runtime sur affirms_consent). */
      consent_given_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Lead a refusé la recherche de données publiques → bascule collecte conversationnelle. */
      consent_refused_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Collecte conversationnelle du contenu démarrée (après refus du scraping). */
      content_collection_started_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Contenu minimal récolté auprès du lead → from_scratch peut partir. */
      content_collected_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Preview URL livrée au lead dans la conv. */
      preview_delivered_at: Type.Optional(Type.String({ format: 'date-time' })),
      /**
       * Regénération UNIQUE après correction post-livraison (métier / ville rectifiés
       * par le lead une fois l'aperçu livré). Posé par le runtime au déclenchement ;
       * gate `computeNextAction` — une seule regénération par lead.
       */
      preview_regenerated_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Lead a refusé la preview explicitement. */
      preview_refused_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Webinaire proposé (lien envoyé). */
      webinar_proposed_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Pivot affiliation Tier 4 émis. */
      affiliation_pitched_at: Type.Optional(Type.String({ format: 'date-time' })),
      /** Confirmation métier demandée par Pulse — évite la boucle "PO c'est bien ça ?" 4 fois. */
      metier_confirmation_asked_at: Type.Optional(Type.String({ format: 'date-time' })),
    }),
  ),

  // === Attribution marketing (first-touch, cf. tracking/lead-funnel.ts) ===
  /**
   * Origine publicitaire du lead : UTM + identifiants Meta/Google (widget web) ou
   * `ctwa_clid` (Click-to-WhatsApp). Posée au premier contact, jamais écrasée.
   * Consommée par LeadMarketingService (CAPI Meta + PostHog server-side).
   */
  attribution: Type.Optional(LeadAttributionSchema),

  // === Audit ===
  blockers: Type.Optional(Type.Array(Type.String({ maxLength: 500 }), { maxItems: 20 })),
  analyzed_at: Type.Optional(Type.String({ format: 'date-time' })),
});

export type LeadQualification = Static<typeof LeadQualificationSchema>;

// =====================================================================
// Pulse — Intent classification (pré-LLM, archi hybrid Chantier 1).
//
// À chaque tour de conversation, le runtime invoque un LLM rapide (gemma3n
// ou mistral3) qui retourne ce JSON structuré à partir du message courant +
// historique. Le runtime utilise ensuite ce JSON pour :
//   - router vers le bon micro-prompt focalisé (`next_state`)
//   - injecter ce qu'on sait déjà du lead dans le prompt compositeur
//     (`infos_deja_donnees`) pour éviter les questions redondantes
//   - déclencher l'escalade transversale si `wants_human` ou
//     `shows_frustration` (avec progression N1 → N2 → N3 selon le compteur
//     de demandes humain)
// =====================================================================

/** Profil "infos déjà données" — ce que le runtime sait du lead à ce tour. */
export const PulseLeadKnownInfoSchema = Type.Object({
  nom_pro: Type.Optional(Type.String({ maxLength: 200 })),
  metier: Type.Optional(Type.String({ maxLength: 120 })),
  ville: Type.Optional(Type.String({ maxLength: 120 })),
  anciennete: Type.Optional(Type.String({ maxLength: 60 })),
  /** Signaux patrimoine bruts détectés (« fiche google », « planity », « instagram », « papier », ...). */
  patrimoine_signaux: Type.Optional(Type.Array(Type.String({ maxLength: 80 }), { maxItems: 12 })),
  /** Citation directe du lead exprimant une douleur (sert pour le Aha au tour 3). */
  douleur_signal: Type.Optional(Type.String({ maxLength: 500 })),
  /** Liens collés par le lead (sites, réseaux, plateformes) — exploités par ingest_url plus tard. */
  links_collected: Type.Optional(Type.Array(Type.String({ maxLength: 500 }), { maxItems: 10 })),
  /** Prestations mentionnées en clair (« coupe », « consultation 1h », « course aéroport », ...). */
  prestations_mentionnees: Type.Optional(Type.Array(Type.String({ maxLength: 120 }), { maxItems: 10 })),
});
export type PulseLeadKnownInfo = Static<typeof PulseLeadKnownInfoSchema>;

const tierUnionWithUnknown = Type.Union([
  Type.Literal(LeadTier.TIER_1_BEAUTE),
  Type.Literal(LeadTier.TIER_1_BIEN_ETRE),
  Type.Literal(LeadTier.TIER_1_VTC),
  Type.Literal(LeadTier.TIER_2_GENERIQUE),
  Type.Literal(LeadTier.TIER_3_MEDICAL),
  Type.Literal(LeadTier.TIER_4_LOST),
  Type.Literal('unknown'),
]);

/**
 * Pulse — schéma de sortie de l'intent detector (refonte spec cible §9.4).
 *
 * Réduit de 22 champs à 7 champs structurés + sous-objet `infos_extracted`.
 * Supprime : lead_orientation, posture, douleur_inferee, lead_orientation_history,
 * flags secondaires non utilisés.
 *
 * Ajoute : `is_out_of_scope` + `out_of_scope_category` (§12.2 hors-scope).
 */
export const PulseLeadInfosExtractedSchema = Type.Object({
  /** Verbatim métier ("esthé", "kiné", "coiffeur"). */
  metier_raw: Type.Optional(Type.String({ maxLength: 120 })),
  /**
   * Libellé métier NORMALISÉ par le LLM (orthographe corrigée, forme canonique) :
   * "koifeur" → "coiffeur", "esthé" → "esthéticienne". Les leads écrivent en style
   * SMS plein de fautes — `metier_raw` garde le verbatim, ce champ alimente
   * `qualification.metier` (affiché en admin, injecté dans le contenu généré).
   */
  metier_canonique: Type.Optional(Type.String({ maxLength: 120 })),
  ville: Type.Optional(Type.String({ maxLength: 120 })),
  /**
   * Nom de l'établissement tel que cité par le lead ("mon salon s'appelle Éclat",
   * "L'Atelier de Marie"). Clé du lookup Google Places (iso onboarding) : sans nom
   * on ne peut pas récupérer la fiche GMB → repli synthèse from_scratch.
   */
  nom_pro: Type.Optional(Type.String({ maxLength: 200 })),
  /** Le lead a mentionné une fiche Google Business dans son message ? */
  google_business_mentioned: Type.Optional(Type.Boolean()),
  google_business_url: Type.Optional(Type.String({ maxLength: 500 })),
  website_mentioned: Type.Optional(Type.Boolean()),
  website_url: Type.Optional(Type.String({ maxLength: 500 })),
  booking_platform_mentioned: Type.Optional(Type.Boolean()),
  booking_platform_url: Type.Optional(Type.String({ maxLength: 500 })),
  prestations_mentionnees: Type.Optional(Type.Array(Type.String({ maxLength: 120 }), { maxItems: 12 })),
  /**
   * Description libre de l'activité fournie par le lead (chemin collecte
   * conversationnelle quand il a refusé le scraping). Sert de base bio en
   * génération from_scratch. Cf. canon `collect_content`.
   */
  business_description: Type.Optional(Type.String({ maxLength: 2000 })),
  /**
   * Prestations structurées avec prix/durée quand le lead les énonce ("coupe
   * 30€", "massage 1h 60€"). Alimente `qualification.prestations_detailed` →
   * pitch concret + génération from_scratch sur contenu fourni.
   */
  prestations_detailed: Type.Optional(
    Type.Array(
      Type.Object({
        name: Type.String({ maxLength: 120 }),
        price_eur: Type.Optional(Type.Number()),
        duration_min: Type.Optional(Type.Number()),
      }),
      { maxItems: 20 },
    ),
  ),
  /** Signaux de besoin captés en clair (ex: "dependance_plateforme", "visibilite"). */
  signaux_besoin_detectes: Type.Optional(Type.Array(Type.String({ maxLength: 80 }), { maxItems: 12 })),
  /** Signaux de douleur captés en clair (ex: "no_show", "marge_grignotee"). */
  signaux_douleur_detectes: Type.Optional(Type.Array(Type.String({ maxLength: 80 }), { maxItems: 12 })),
  /**
   * Le lead est en LANCEMENT / CRÉATION d'activité : il crée son entreprise, se
   * lance, démarre tout juste — et n'a PAS encore de présence en ligne (ni site,
   * ni fiche Google, ni plateforme de résa). Champ extrait DIRECTEMENT par le LLM
   * (robuste aux fautes / paraphrases, là où une regex échoue). Sémantiquement
   * DISTINCT de `lead_says_nothing` (= le lead esquive / ne donne rien) : ici le
   * lead AFFIRME clairement qu'il démarre. Le runtime en dérive `patrimoine='rien'`
   * → l'escalier bascule sur le cadrage « option pour VISUALISER ton futur site »
   * au lieu de « je récupère tes données » (il n'y a rien à scraper).
   */
  is_launching: Type.Optional(Type.Boolean()),
});
export type PulseLeadInfosExtracted = Static<typeof PulseLeadInfosExtractedSchema>;

export const PulseIntentClassificationSchema = Type.Object({
  /**
   * @deprecated Refonte spec cible 2026-05-25 (Lot 1.4) : la phase est désormais
   * calculée par `computePhase(collectedData)` côté code, pas décidée par le LLM.
   * Le champ reste dans le schéma le temps que le runtime soit migré (Lot 1.5) —
   * il est rempli en code via `mapPhaseToQualifierState(computePhase(qualif))`,
   * jamais retourné par le LLM.
   */
  next_state: Type.Union([
    Type.Literal(PulseQualifierState.PHASE_1_COMPRENDRE),
    Type.Literal(PulseQualifierState.PHASE_2_DEMONTRER),
    Type.Literal(PulseQualifierState.PHASE_3_INVITER),
    Type.Literal(PulseQualifierState.PHASE_5_ESCALATE),
  ]),
  /** @deprecated voir `next_state`. Reste pour audit pendant la migration. */
  next_state_reason: Type.String({ maxLength: 300 }),

  // === Tier (peut amender la pré-classif déterministe) ===
  tier: tierUnionWithUnknown,
  tier_confidence: Type.Number({ minimum: 0, maximum: 1 }),

  // === Extraction continue (alimente lead.qualification au fil de l'eau) ===
  infos_extracted: PulseLeadInfosExtractedSchema,

  // === Flags décisionnels — le minimum vital ===
  /** Lead demande explicitement un humain ("je veux parler à quelqu'un"). */
  wants_human: Type.Boolean(),
  /** Ton agressif, harcèlement, propos déplacés. Déclenche [[ESCALATE:lead_agressif]]. */
  shows_aggression: Type.Boolean(),
  /** Lead demande le prix Propelsia ("c'est combien", "vos tarifs"). */
  asks_price: Type.Boolean(),
  /**
   * Lead pose une question PRODUIT (pas prix) : "ça gère les rdv en ligne ?",
   * "vous faites des emails marketing ?", "y a un agenda intégré ?". Ajouté
   * Lot 1.4 — distinct de `asks_price` pour permettre des briefs séparés
   * (`knowledge: "product"` vs `knowledge: "pricing"`).
   */
  asks_product: Type.Boolean(),
  /** Lead demande comment s'inscrire / signer. */
  asks_signup: Type.Boolean(),
  /**
   * OBJECTION de fond exprimée par le lead (refonte vente-conseil Lot 2) —
   * distincte d'une simple question. Le dispatcher route vers un canon dédié
   * qui traite l'objection À LA RACINE (valeur/ROI/différenciant/réassurance)
   * au lieu de l'esquiver vers la preview ou le webinaire.
   *  - `price`          : « c'est trop cher », « pas les moyens », « budget serré »
   *  - `has_website`    : « j'ai déjà un site », « je vois pas ce que ça apporte de plus »
   *  - `no_time`        : « j'ai pas le temps », « trop occupé pour me lancer là-dedans »
   *  - `migration_fear` : « peur que ce soit compliqué », « tout refaire / migrer »
   */
  objection: Type.Optional(
    Type.Union([
      Type.Literal('price'),
      Type.Literal('has_website'),
      Type.Literal('no_time'),
      Type.Literal('migration_fear'),
      // Le prompt demande au LLM d'émettre `null` quand il n'y a pas d'objection.
      // `Type.Optional` autorise l'ABSENCE du champ mais PAS la valeur `null` →
      // sans `Type.Null()` un `"objection": null` fait échouer toute la validation
      // TypeBox → fallback (perte de l'extraction du tour). On accepte donc null.
      Type.Null(),
    ]),
  ),
  /** Lead refuse explicitement la preview ("non pas envie de voir"). */
  refuses_preview: Type.Boolean(),
  /**
   * Lead accepte EXPLICITEMENT que Pulse cherche ses données publiques pour
   * monter la preview ("oui vas-y", "ok cherche", "je t'autorise", "c'est mon
   * activité"). Consent-first : ce flag est la SEULE porte qui déclenche le
   * scraping après le tour `ask_consent` — un simple non-refus ne suffit plus.
   * Lu uniquement quand `pulse_journey.consent_asked_at` est posé (un "oui"
   * générique ailleurs reste donc inoffensif).
   */
  affirms_consent: Type.Boolean(),
  /**
   * Consentement-données EXPLICITE et SPONTANÉ à la récupération des infos
   * publiques (« tu peux chercher mes infos publiques », « je t'autorise à
   * récupérer mes données »), distinct de l'enthousiasme-preview (« vas-y
   * montre-moi »). Posé déterministiquement (regex étroite). Sert UNIQUEMENT à
   * court-circuiter le tour `ask_consent` quand le lead a déjà donné un accord
   * data clair AVANT qu'on le demande : on enchaîne alors directement sur la
   * génération au lieu de re-demander. Un simple « montre-moi » NE le lève PAS
   * (il doit passer par ask_consent, qui laisse refuser le scraping — consent-first).
   */
  explicit_data_consent: Type.Optional(Type.Boolean()),
  /**
   * Lead réagit à la preview DÉJÀ livrée en demandant un changement visuel
   * ("j'aime bien mais je préfère le rouge", "tu peux changer la photo / le
   * texte / le logo / la police"). N'est posé qu'une fois la preview livrée
   * (cf. `hasPreviewReady` dans mergeDeterministicFlags) : c'est un signal
   * d'engagement fort, jamais du hors-scope. Le dispatcher route vers le canon
   * `preview_feedback` (accuse réception + personnalisation post-inscription + CTA).
   */
  feedback_on_preview: Type.Optional(Type.Boolean()),
  /**
   * Signaux de routage hors tunnel (corpus prod 10/2026) : demandés au LLM (prompt classifieur)
   * ET plancher regex `intent/lead-signals.ts` dans tous les modes de détection — OR pur, le
   * runtime ne laisse aucun des deux écraser l'autre :
   *  - `existing_customer` : client déjà chez nous (ancien nom Cfixé, « connecter à mon
   *    compte », « mes récurrences ne passent pas », mot de passe…) → orientation espace pro /
   *    support, jamais de qualification ni d'aperçu ;
   *  - `wants_to_stop` : arrêt explicite de la conversation (« on reste là », « laisse
   *    tomber », « je ne veux pas de site ») → clôture polie sans CTA ni webinaire ;
   *  - `dislikes_preview` : « j'aime pas » sur l'aperçu livré → on demande ce qui ne va pas,
   *    sans CTA ;
   *  - `wants_to_think` : « je vais réfléchir » → porte ouverte + session de présentation.
   */
  existing_customer: Type.Optional(Type.Boolean()),
  wants_to_stop: Type.Optional(Type.Boolean()),
  dislikes_preview: Type.Optional(Type.Boolean()),
  wants_to_think: Type.Optional(Type.Boolean()),
  /**
   * Intérêt pour le programme ambassadeur (recette 16/10/2026, C2) : recommander / parrainer, une
   * audience (« mes abonnées »), « ça rapporte ? ». Plancher déterministe dans tous les modes ;
   * routé vers `knowledge: affiliation` quel que soit le tier.
   */
  asks_affiliation: Type.Optional(Type.Boolean()),
  /** Lead demande un exemple / cas client. */
  asks_for_example: Type.Boolean(),
  /** Lead dit "je démarre, j'ai rien" ou équivalent (réponse négative valide patrimoine). */
  lead_says_nothing: Type.Boolean(),
  /**
   * Lead envoie un accusé de réception vide de contenu : "ok", "j'attends",
   * "👍", "parfait", "merci". Ajouté Lot 1.4 — utilisé par le dispatcher pour
   * court-circuiter le compositeur quand `preview_generation_pending=true`
   * (cas "silence pendant l'attente de la preview"). En dehors de ce cas,
   * le flag est informatif (le compositeur peut choisir de répondre court).
   */
  expects_no_reply: Type.Boolean(),

  // === Hors-scope (§12.2 spec) ===
  /** Le message dévie hors du scope Pulse (recette, code, culture gé, etc.). */
  is_out_of_scope: Type.Boolean(),
  /** Catégorie hors-scope si is_out_of_scope=true. */
  out_of_scope_category: Type.Optional(
    Type.Union([
      Type.Literal('general_knowledge'),
      Type.Literal('coding'),
      Type.Literal('creative_writing'),
      Type.Literal('personal_advice'),
      Type.Literal('homework'),
      Type.Literal('competitor_product'),
      Type.Literal('ai_jailbreak'),
      Type.Literal('recipe_or_lifestyle'),
      Type.Literal('other'),
    ]),
  ),

  // === Détection « full LLM » (plan pulse-full-llm-detection-plan.md, lot A.2) ===
  // Tous OPTIONNELS : l'ancien modèle / l'ancien prompt restent valides pendant la transition.
  /** Coordonnées écrites en clair par le lead (email / téléphone) — jamais du hors-scope. */
  contact_info: Type.Optional(
    Type.Object({
      email: Type.Optional(Type.String({ maxLength: 200 })),
      phone: Type.Optional(Type.String({ maxLength: 40 })),
    }),
  ),
  /**
   * Confiance 0..1 du hors-scope. Le court-circuit hors-scope est la décision la plus
   * destructrice du pipeline : en mode `llm`, un hors-scope LLM non corroboré par le
   * filet déterministe n'est appliqué qu'au-dessus d'un seuil (cf. `mergeDeterministicFlags`).
   */
  out_of_scope_confidence: Type.Optional(Type.Number({ minimum: 0, maximum: 1 })),
  /**
   * Le lead CORRIGE une info donnée précédemment (« en fait je suis à Vence », « non pas
   * coach, kiné »). Équivalent LLM du marqueur regex `hasCorrectionMarker` : autorise le
   * remplacement d'un métier / d'une ville déjà connus.
   */
  corrects_previous_info: Type.Optional(Type.Boolean()),
  /**
   * Activité hors cœur de cible où des clients prennent quand même RDV (boutique qui prend des
   * RDV, avocat, agence immobilière, école privée, consultant avec RDV découverte) → tier 2.
   */
  cible_elargie: Type.Optional(Type.Boolean()),
  /** Activité exclue sans appel (services sexuels, illégal, contenus adultes) → tier 4 verrouillé. */
  activite_exclue: Type.Optional(Type.Boolean()),
  /** Extrait du message qui justifie les flags levés (debug + juge d'éval). */
  signals_evidence: Type.Optional(Type.String({ maxLength: 500 })),

  // === Audit ===
  analyzed_at: Type.String({ format: 'date-time' }),
});
export type PulseIntentClassification = Static<typeof PulseIntentClassificationSchema>;

// =====================================================================
// Card translation keys — backend selects a key, frontend renders via t().
// Keep in sync with the ASSISTANT namespace in `translation/fr-FR.ts`.
// =====================================================================

export const ASSISTANT_CARD_TITLE_KEYS = ['CARD_SUPPORT_TITLE'] as const;
export type AssistantCardTitleKey = (typeof ASSISTANT_CARD_TITLE_KEYS)[number];

export const ASSISTANT_CARD_DESCRIPTION_KEYS = ['CARD_SUPPORT_DESCRIPTION'] as const;
export type AssistantCardDescriptionKey = (typeof ASSISTANT_CARD_DESCRIPTION_KEYS)[number];

export const ASSISTANT_CARD_ACTION_KEYS = ['CARD_SUPPORT_OPEN'] as const;
export type AssistantCardActionKey = (typeof ASSISTANT_CARD_ACTION_KEYS)[number];

// =====================================================================
// Tool calls — emitted by the LLM via OpenAI/Mistral function-calling.
// Persisted into tool_invocation rows. Confirmation cards are sent back to
// the front-end for non-GET tools that require explicit user approval.
// =====================================================================

export const AssistantToolCallSchema = Type.Object({
  call_id: Type.String({ minLength: 1, maxLength: 128 }),
  name: Type.String({ minLength: 1, maxLength: 128 }),
  args: Type.Record(Type.String(), Type.Unknown()),
});

export const AssistantToolConfirmationCardSchema = Type.Object({
  tool_invocation_id: Type.Number(),
  /**
   * Identifiant du tool_call côté fournisseur LLM. Il n'a de sens que dans le tour
   * qui l'a produit : une carte reconstruite depuis l'historique (getConversation)
   * n'en a pas. `tool_invocation_id` est la seule clé stable d'une carte.
   */
  call_id: Type.Optional(Type.String()),
  tool_name: Type.String(),
  summary: Type.String({ maxLength: 500 }),
  args: Type.Record(Type.String(), Type.Unknown()),
  http_method: Type.String(),
  path: Type.String(),
});

export type AssistantToolCall = Static<typeof AssistantToolCallSchema>;
export type AssistantToolConfirmationCard = Static<typeof AssistantToolConfirmationCardSchema>;

// =====================================================================
// Attachments — images + voice notes posted alongside a user message.
// Files are uploaded to S3 first via /app/bucket/presign; only the s3_key
// is sent in the payload. Audio is transcribed server-side (Whisper) and
// the transcript becomes the effective text content seen by the LLM.
// =====================================================================

export enum AssistantAttachmentType {
  IMAGE = 'IMAGE',
  AUDIO = 'AUDIO',
}

export const ASSISTANT_ATTACHMENT_MAX_PER_MESSAGE = 5;
export const ASSISTANT_ATTACHMENT_MAX_BYTES_IMAGE = 10 * 1024 * 1024;
export const ASSISTANT_ATTACHMENT_MAX_BYTES_AUDIO = 25 * 1024 * 1024;
export const ASSISTANT_ATTACHMENT_ALLOWED_IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;
export const ASSISTANT_ATTACHMENT_ALLOWED_AUDIO_MIME = [
  'audio/webm',
  'audio/ogg',
  'audio/mpeg',
  'audio/mp3',
  'audio/mp4',
  'audio/wav',
  'audio/x-wav',
] as const;

export const AssistantAttachmentInputSchema = Type.Object({
  type: Type.Union([Type.Literal(AssistantAttachmentType.IMAGE), Type.Literal(AssistantAttachmentType.AUDIO)]),
  s3_key: Type.String({ minLength: 1, maxLength: 512 }),
  filename: Type.Optional(Type.String({ maxLength: 256 })),
  mime: Type.String({ minLength: 1, maxLength: 128 }),
  size: Type.Number({ minimum: 0, maximum: 50 * 1024 * 1024 }),
  duration_ms: Type.Optional(Type.Number({ minimum: 0, maximum: 10 * 60 * 1000 })),
});

export const AssistantAttachmentSchema = Type.Object({
  type: Type.Union([Type.Literal(AssistantAttachmentType.IMAGE), Type.Literal(AssistantAttachmentType.AUDIO)]),
  s3_key: Type.String({ minLength: 1, maxLength: 512 }),
  filename: Type.Optional(Type.String({ maxLength: 256 })),
  mime: Type.String({ minLength: 1, maxLength: 128 }),
  size: Type.Number({ minimum: 0, maximum: 50 * 1024 * 1024 }),
  duration_ms: Type.Optional(Type.Number({ minimum: 0, maximum: 10 * 60 * 1000 })),
  url: Type.Optional(Type.String({ maxLength: 2048 })),
  transcript: Type.Optional(Type.String({ maxLength: 8000 })),
});

export type AssistantAttachmentInput = Static<typeof AssistantAttachmentInputSchema>;
export type AssistantAttachment = Static<typeof AssistantAttachmentSchema>;

// =====================================================================
// Existing chat I/O contracts (request body, response payload)
// =====================================================================

export const AssistantPageContextSchema = Type.Object({
  path: Type.Optional(Type.String({ maxLength: 500 })),
  title: Type.Optional(Type.String({ maxLength: 200 })),
});

/**
 * Onboarding v4, lot 5 : d'où le pro parle à Pulse pendant le tunnel.
 * - `onboarding_tunnel` : depuis un écran du tunnel sans étape précise (reprise, fin) ;
 * - `onboarding_step` : depuis une étape (`step_id`), écran 10 ou sortie « le dire à Pulse ».
 * Le runtime route alors sur le playbook `onboarding_v1` ; un pro v2 dont le tunnel n'est pas fini
 * y est routé même sans ce champ.
 */
export enum AssistantConversationContext {
  ONBOARDING_TUNNEL = 'onboarding_tunnel',
  ONBOARDING_STEP = 'onboarding_step',
}

/**
 * Identifiants des étapes du tunnel acceptés dans `step_id` — recopiés ici (et verrouillés par
 * `assistant.spec`) plutôt qu'importés : `onboarding.schema.ts` importe déjà ce fichier.
 */
export const ASSISTANT_TUNNEL_STEP_IDS = ['offer', 'places_hours', 'share', 'website', 'seo', 'payment'] as const;

export const AssistantMessageBodySchema = Type.Object({
  message: Type.String({ minLength: 0, maxLength: 4000 }),
  page: Type.Optional(AssistantPageContextSchema),
  attachments: Type.Optional(
    Type.Array(AssistantAttachmentInputSchema, { maxItems: ASSISTANT_ATTACHMENT_MAX_PER_MESSAGE }),
  ),
  context: Type.Optional(
    Type.Union(
      Object.values(AssistantConversationContext).map((value): ReturnType<typeof Type.Literal> => Type.Literal(value)),
    ),
  ),
  step_id: Type.Optional(
    Type.Union(ASSISTANT_TUNNEL_STEP_IDS.map((value): ReturnType<typeof Type.Literal> => Type.Literal(value))),
  ),
});

/**
 * Qui a écrit une ligne du compte (`prestation.origin`, `catalog_category.origin`,
 * `pro_place.origin`, `pro_generated_text.origin`). Null = le pro lui-même (valeur historique) ;
 * `pulse` = écriture confirmée par le pro depuis une carte Pulse (`origin_conversation_id` dit où).
 */
export enum WriteOrigin {
  PRO = 'pro',
  PULSE = 'pulse',
}

export const AssistantCardSchema = Type.Object({
  type: Type.Union([Type.Literal('draft'), Type.Literal('insight'), Type.Literal('action')]),
  title_key: Type.Union(ASSISTANT_CARD_TITLE_KEYS.map((key): ReturnType<typeof Type.Literal> => Type.Literal(key))),
  description_key: Type.Optional(
    Type.Union(ASSISTANT_CARD_DESCRIPTION_KEYS.map((key): ReturnType<typeof Type.Literal> => Type.Literal(key))),
  ),
  description_values: Type.Optional(Type.Record(Type.String(), Type.Union([Type.String(), Type.Number()]))),
  actions: Type.Optional(
    Type.Array(
      Type.Object({
        label_key: Type.Union(
          ASSISTANT_CARD_ACTION_KEYS.map((key): ReturnType<typeof Type.Literal> => Type.Literal(key)),
        ),
        href: Type.Optional(Type.String()),
      }),
    ),
  ),
});

export const AssistantMessageResponseSchema = Type.Object({
  message: Type.String(),
  cards: Type.Optional(Type.Array(AssistantCardSchema)),
  /**
   * Pending tool confirmations the user must accept/cancel before the runtime
   * actually executes the action. Only populated for non-GET tools.
   */
  tool_confirmations: Type.Optional(Type.Array(AssistantToolConfirmationCardSchema)),
  provider: Type.Optional(Type.Union([Type.Literal('fallback'), Type.Literal('llm')])),
  debug: Type.Optional(
    Type.Object({
      model_profile: Type.Optional(Type.String()),
      model: Type.Optional(Type.String()),
      prompt_tokens: Type.Optional(Type.Number()),
      completion_tokens: Type.Optional(Type.Number()),
      total_tokens: Type.Optional(Type.Number()),
      estimated_cost_eur: Type.Optional(Type.Number()),
    }),
  ),
});

// =====================================================================
// Conversation history endpoint (GET /app/assistant/conversation)
// =====================================================================

export const AssistantHistoryMessageSchema = Type.Object({
  message_id: Type.Number(),
  role: Type.Union([
    Type.Literal(MessageRole.USER),
    Type.Literal(MessageRole.ASSISTANT),
    Type.Literal(MessageRole.SYSTEM),
    Type.Literal(MessageRole.TOOL),
  ]),
  channel: Type.Union([
    Type.Literal(MessageChannel.WEB),
    Type.Literal(MessageChannel.WEB_WIDGET),
    Type.Literal(MessageChannel.WHATSAPP),
    Type.Literal(MessageChannel.MESSENGER),
    Type.Literal(MessageChannel.INSTAGRAM),
    Type.Literal(MessageChannel.EMAIL),
    Type.Literal(MessageChannel.SYSTEM),
  ]),
  content: Type.String(),
  attachments: Type.Optional(Type.Array(AssistantAttachmentSchema)),
  created_at_formatted: Type.String({ format: 'date-time', meta: { source_key: 'created_at', with_time: true } }),
});

export const AssistantConversationResponseSchema = Type.Object({
  conversation_id: Type.Number(),
  active_playbook: Type.Optional(Type.String()),
  messages: Type.Array(AssistantHistoryMessageSchema),
  /**
   * Cartes de confirmation encore en attente sur ce fil. Sur un tour d'écriture, la
   * bulle assistant est vide par construction (le texte vit SUR la carte) : sans ce
   * champ, un rechargement de page laissait une bulle vide et une écriture orpheline.
   * Seules les cartes non expirées sont renvoyées — au-delà d'une heure, le cron de
   * purge les passe en REFUSED.
   */
  tool_confirmations: Type.Optional(Type.Array(AssistantToolConfirmationCardSchema)),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});

// =====================================================================
// Playbook contract (in-code definition, not DB-backed yet)
// =====================================================================

export type Playbook = {
  id: string;
  version: string;
  applicable_states: LeadState[];
  system_prompt: string;
  allowed_tools: string[];
  success_criteria?: string;
  next_playbook_hint?: string;
};

// =====================================================================
// Public widget I/O — used by the anonymous landing-page chat (no JWT).
// The visitor is identified by a UUID stored in a cookie + sent with each
// request. Lead is created on first message with state=COLD.
// =====================================================================

const VISITOR_TOKEN_PATTERN = '^[A-Za-z0-9_-]{16,64}$';

export const AssistantWidgetUtmSchema = Type.Object({
  source: Type.Optional(Type.String({ maxLength: 128 })),
  medium: Type.Optional(Type.String({ maxLength: 128 })),
  campaign: Type.Optional(Type.String({ maxLength: 128 })),
  content: Type.Optional(Type.String({ maxLength: 256 })),
  term: Type.Optional(Type.String({ maxLength: 256 })),
  referrer: Type.Optional(Type.String({ maxLength: 500 })),
  // Identifiants pub navigateur, capturés au 1er message widget puis persistés en
  // first-touch dans `lead.qualification.attribution` (cf. tracking/lead-funnel.ts).
  // Indispensables pour renvoyer plus tard des conversions CAPI attribuables.
  fbclid: Type.Optional(Type.String({ maxLength: 512 })),
  fbp: Type.Optional(Type.String({ maxLength: 128 })),
  fbc: Type.Optional(Type.String({ maxLength: 256 })),
  gclid: Type.Optional(Type.String({ maxLength: 512 })),
  // Identifiants de clic des autres régies, capturés dès MAINTENANT même sans
  // campagne active : un clic non capturé n'est jamais rattrapable (les premiers
  // jours d'une future campagne TikTok/LinkedIn/Bing seraient non attribuables).
  // ttclid (TikTok, URL) / ttp (cookie `_ttp` du Pixel TikTok — soumis à
  // consentement) / li_fat_id (LinkedIn) / msclkid (Microsoft Ads) / epik (Pinterest).
  ttclid: Type.Optional(Type.String({ maxLength: 512 })),
  ttp: Type.Optional(Type.String({ maxLength: 128 })),
  // oppref (OpenAI Ads, URL) / obref (cookie `__obref` du SDK OpenAI — consentement).
  oppref: Type.Optional(Type.String({ maxLength: 512 })),
  obref: Type.Optional(Type.String({ maxLength: 128 })),
  li_fat_id: Type.Optional(Type.String({ maxLength: 512 })),
  msclkid: Type.Optional(Type.String({ maxLength: 512 })),
  epik: Type.Optional(Type.String({ maxLength: 512 })),
  landing_page: Type.Optional(Type.String({ maxLength: 1024 })),
  // Consentement marketing lu sur le cookie cross-sous-domaine posé par la bannière
  // de la vitrine. Il pilote le NIVEAU de détail envoyé à Meta (advanced matching
  // complet vs conversion anonyme sans PII), pas le fait d'envoyer — cf.
  // `TrackingService.sendLeadFunnelEvent`. Contrairement au reste de l'attribution,
  // il n'est PAS first-touch : le lead peut accepter (ou retirer) après coup.
  marketing_consent: Type.Optional(Type.Boolean()),
});

export const AssistantWidgetMessageBodySchema = Type.Object({
  visitor_token: Type.String({ pattern: VISITOR_TOKEN_PATTERN }),
  message: Type.String({ minLength: 0, maxLength: 4000 }),
  page: Type.Optional(AssistantPageContextSchema),
  utm: Type.Optional(AssistantWidgetUtmSchema),
  attachments: Type.Optional(
    Type.Array(AssistantAttachmentInputSchema, { maxItems: ASSISTANT_ATTACHMENT_MAX_PER_MESSAGE }),
  ),
});

export const AssistantWidgetPresignBodySchema = Type.Object({
  visitor_token: Type.String({ pattern: VISITOR_TOKEN_PATTERN }),
  filename: Type.String({ minLength: 1, maxLength: 256 }),
  type: Type.String({ minLength: 1, maxLength: 128 }),
});
export type AssistantWidgetPresignBody = Static<typeof AssistantWidgetPresignBodySchema>;

export const AssistantWidgetConversationResponseSchema = Type.Object({
  conversation_id: Type.Optional(Type.Number()),
  messages: Type.Array(AssistantHistoryMessageSchema),
  total: Type.Number(),
  // Génération de preview en cours (waouh v2 §3.3) : le widget affiche une bulle de
  // progression avec étapes nommées + polling rapide tant que `pending` est vrai.
  preview_generation_pending: Type.Optional(Type.Boolean()),
  // Étape courante (cf. PREVIEW_GENERATION_STAGES dans lead-preview.ts) — le wording
  // affiché dépend des étapes réellement posées par le processor (honnêteté par mode).
  preview_generation_stage: Type.Optional(PreviewGenerationStageSchema),
});

// =====================================================================
// Backwards: keep AssistantChatMessage type so existing imports still
// resolve. The shape mirrors what the LLM call expects internally.
// =====================================================================

export const AssistantChatMessageSchema = Type.Object({
  role: Type.Union([Type.Literal('user'), Type.Literal('assistant')]),
  content: Type.String({ maxLength: 4000 }),
});

export type AssistantChatMessage = Static<typeof AssistantChatMessageSchema>;
export type AssistantPageContext = Static<typeof AssistantPageContextSchema>;
export type AssistantMessageBody = Static<typeof AssistantMessageBodySchema>;
export type AssistantCard = Static<typeof AssistantCardSchema>;
export type AssistantMessageResponse = Static<typeof AssistantMessageResponseSchema>;
export type AssistantHistoryMessage = Static<typeof AssistantHistoryMessageSchema>;
export type AssistantConversationResponse = Static<typeof AssistantConversationResponseSchema>;
export type AssistantWidgetUtm = Static<typeof AssistantWidgetUtmSchema>;
export type AssistantWidgetMessageBody = Static<typeof AssistantWidgetMessageBodySchema>;
export type AssistantWidgetConversationResponse = Static<typeof AssistantWidgetConversationResponseSchema>;
